import React, { useState, useEffect } from 'react';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CButton,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CContainer,
  CRow,
  CCol,
  CBadge,
  CSpinner,
  CAlert,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSearch, cilInfo, cilDollar } from '@coreui/icons';
import { resellerAPI } from '../services/api';

const SoftwareProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('commercial');
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Highlight component for search term
  const Highlight = ({ text, highlight }) => {
    if (!highlight || !highlight.trim()) {
      return <>{text}</>;
    }
    const searchRegex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.toString().split(searchRegex);
    return (
      <>
        {parts.map((part, i) =>
          searchRegex.test(part) ? <mark key={i}>{part}</mark> : part
        )}
      </>
    );
  };

  useEffect(() => {
    fetchSoftwareProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [searchTerm, products, activeTab]);

  const filterProducts = () => {
    let filtered = products;

    // Filter by market segment
    if (activeTab === 'gsa') {
      filtered = filtered.filter(product => product.market === 'GSA');
    } else if (activeTab === 'commercial') {
      filtered = filtered.filter(product => product.market === 'Commercial');
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.software_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.subscription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.subscription_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.softwareid?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by type: NEW first, then Support, then Cartridge
    filtered.sort((a, b) => {
      const typeOrder = { 'NEW': 1, 'Support': 2, 'Cartridge': 3 };
      return typeOrder[a.type] - typeOrder[b.type];
    });

    setFilteredProducts(filtered);
  };

  const fetchSoftwareProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await resellerAPI.getAllProducts();
      
      if (response.data.success) {
        setProducts(response.data.products);
      } else {
        setError('Failed to fetch products');
      }
    } catch (err) {
      console.error('Error fetching software products:', err);
      setError('Failed to load software products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'NEW':
        return 'success';
      case 'Support':
        return 'info';
      case 'Cartridge':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getSubscriptionTypeBadge = (subscriptionType) => {
    switch (subscriptionType) {
      case 'Perpetual':
        return 'dark';
      case 'Annual':
        return 'primary';
      default:
        return 'secondary';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  // Calculate market-specific products
  const gsaProducts = products.filter(p => p.market === 'GSA');
  const commercialProducts = products.filter(p => p.market === 'Commercial');

  // Group products by type for current market
  const groupProductsByType = (productsToGroup) => {
    const grouped = {
      'NEW': productsToGroup.filter(p => p.type === 'NEW'),
      'Support': productsToGroup.filter(p => p.type === 'Support'),
      'Cartridge': productsToGroup.filter(p => p.type === 'Cartridge')
    };
    return grouped;
  };

  const groupedCommercialProducts = groupProductsByType(commercialProducts);
  const groupedGsaProducts = groupProductsByType(gsaProducts);

  if (loading) {
    return (
      <CContainer fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <CSpinner color="primary" size="sm" />
      </CContainer>
    );
  }

  if (error) {
    return (
      <CContainer fluid>
        <CAlert color="danger" className="mb-4">
          <Highlight text={error} highlight={searchTerm} />
          <CButton color="primary" size="sm" className="ms-3" onClick={fetchSoftwareProducts}>
            Retry
          </CButton>
        </CAlert>
      </CContainer>
    );
  }

  const renderProductsTable = (productsToShow) => (
    <CTable responsive hover>
      <CTableHead>
        <CTableRow>
          <CTableHeaderCell>Software ID</CTableHeaderCell>
          <CTableHeaderCell>Product Name</CTableHeaderCell>
          <CTableHeaderCell>Subscription</CTableHeaderCell>
          <CTableHeaderCell>Type</CTableHeaderCell>
          <CTableHeaderCell>Subscription Type</CTableHeaderCell>
          <CTableHeaderCell>Price</CTableHeaderCell>
          <CTableHeaderCell>Actions</CTableHeaderCell>
        </CTableRow>
      </CTableHead>
      <CTableBody>
        {productsToShow.map(product => (
          <CTableRow key={product.id}>
            <CTableDataCell>
              <code><Highlight text={product.softwareid} highlight={searchTerm} /></code>
            </CTableDataCell>
            <CTableDataCell>
              <strong><Highlight text={product.software_name} highlight={searchTerm} /></strong>
            </CTableDataCell>
            <CTableDataCell>
              <div className="text-wrap" style={{ maxWidth: '300px' }}>
                <Highlight text={product.subscription} highlight={searchTerm} />
              </div>
            </CTableDataCell>
            <CTableDataCell>
              <CBadge color={getTypeBadge(product.type)}>
                <Highlight text={product.type} highlight={searchTerm} />
              </CBadge>
            </CTableDataCell>
            <CTableDataCell>
              <CBadge color={getSubscriptionTypeBadge(product.subscription_type)}>
                <Highlight text={product.subscription_type} highlight={searchTerm} />
              </CBadge>
            </CTableDataCell>
            <CTableDataCell>
              <div className="d-flex align-items-center">
                <CIcon icon={cilDollar} className="text-success me-1" />
                <strong>{formatPrice(product.price)}</strong>
              </div>
            </CTableDataCell>
            <CTableDataCell>
              <CButton color="info" size="sm" onClick={() => setSelectedProduct(product)}>
                <CIcon icon={cilInfo} className="me-1" />
                Details
              </CButton>
            </CTableDataCell>
          </CTableRow>
        ))}
      </CTableBody>
    </CTable>
  );

  const renderGroupedProductsTable = (groupedProducts, market) => {
    const types = ['NEW', 'Support', 'Cartridge'];
    
    return (
      <div>
        {types.map(type => {
          const productsOfType = groupedProducts[type];
          if (productsOfType.length === 0) return null;

          return (
            <div key={type} className="mb-4">
              <div className="d-flex align-items-center mb-2">
                <CBadge 
                  color={getTypeBadge(type)} 
                  className="fs-6 px-3 py-2"
                >
                  <Highlight text={type} highlight={searchTerm} />{' '}
                  Products ({productsOfType.length})
                </CBadge>
              </div>
              <CTable responsive hover>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Software ID</CTableHeaderCell>
                    <CTableHeaderCell>Product Name</CTableHeaderCell>
                    <CTableHeaderCell>Subscription</CTableHeaderCell>
                    <CTableHeaderCell>Type</CTableHeaderCell>
                    <CTableHeaderCell>Subscription Type</CTableHeaderCell>
                    <CTableHeaderCell>Price</CTableHeaderCell>
                    <CTableHeaderCell>Actions</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {productsOfType.map(product => (
                    <CTableRow key={product.id}>
                      <CTableDataCell>
                        <code><Highlight text={product.softwareid} highlight={searchTerm} /></code>
                      </CTableDataCell>
                      <CTableDataCell>
                        <strong><Highlight text={product.software_name} highlight={searchTerm} /></strong>
                      </CTableDataCell>
                      <CTableDataCell>
                        <div className="text-wrap" style={{ maxWidth: '300px' }}>
                          <Highlight text={product.subscription} highlight={searchTerm} />
                        </div>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={getTypeBadge(product.type)}>
                          <Highlight text={product.type} highlight={searchTerm} />
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={getSubscriptionTypeBadge(product.subscription_type)}>
                          <Highlight text={product.subscription_type} highlight={searchTerm} />
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell>
                        <div className="d-flex align-items-center">
                          <CIcon icon={cilDollar} className="text-success me-1" />
                          <strong>{formatPrice(product.price)}</strong>
                        </div>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CButton color="info" size="sm" onClick={() => setSelectedProduct(product)}>
                          <CIcon icon={cilInfo} className="me-1" />
                          Details
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <CContainer fluid>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>
            Software Products
          </h2>
          <p className="text-muted">
            Browse our catalog of software products by market segment
          </p>
        </div>
      </div>

      {/* Tabs Navigation - Only GSA and Commercial */}
      <CCard>
        <CCardHeader>
          <CRow className="align-items-center">
            <CCol md={6}>
              <CNav variant="tabs">
                <CNavItem>
                  <CNavLink
                    active={activeTab === 'commercial'}
                    onClick={() => setActiveTab('commercial')}
                  >
                    Commercial Market ({commercialProducts.length})
                  </CNavLink>
                </CNavItem>                
                <CNavItem>
                  <CNavLink
                    active={activeTab === 'gsa'}
                    onClick={() => setActiveTab('gsa')}
                  >
                    GSA Market ({gsaProducts.length})
                  </CNavLink>
                </CNavItem>                
              </CNav>
            </CCol>
            <CCol md={6}>
              <CInputGroup>
                <CInputGroupText>
                  <CIcon icon={cilSearch} />
                </CInputGroupText>
                <CFormInput
                  placeholder={`Search ${activeTab} products...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <CButton 
                    color="secondary" 
                    variant="outline" 
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </CButton>
                )}
              </CInputGroup>
            </CCol>
          </CRow>
        </CCardHeader>
        <CCardBody>
          <CTabContent>
            {/* Commercial Products Tab */}
            <CTabPane visible={activeTab === 'commercial'}>
              {filteredProducts.length > 0 ? (
                renderGroupedProductsTable(groupProductsByType(filteredProducts), 'commercial')
              ) : (
                <div className="text-center p-4">
                  {searchTerm ? (
                    <>
                      <p className="text-muted">
                        No commercial products found matching "{searchTerm}"
                      </p>
                      <CButton color="primary" onClick={() => setSearchTerm('')}>
                        Clear Search
                      </CButton>
                    </>
                  ) : (
                    <p className="text-muted">
                      No commercial products available.
                    </p>
                  )}
                </div>
              )}
            </CTabPane>

            {/* GSA Products Tab */}
            <CTabPane visible={activeTab === 'gsa'}>
              {filteredProducts.length > 0 ? (
                renderGroupedProductsTable(groupProductsByType(filteredProducts), 'gsa')
              ) : (
                <div className="text-center p-4">
                  {searchTerm ? (
                    <>
                      <p className="text-muted">
                        No GSA products found matching "{searchTerm}"
                      </p>
                      <CButton color="primary" onClick={() => setSearchTerm('')}>
                        Clear Search
                      </CButton>
                    </>
                  ) : (
                    <p className="text-muted">
                      No GSA products available.
                    </p>
                  )}
                </div>
              )}
            </CTabPane>
          </CTabContent>
        </CCardBody>
      </CCard>

      {/* Market Information */}
      <CRow className="mt-4">
        <CCol md={6}>
          <CCard>
            <CCardHeader className={`bg-${activeTab === 'gsa' ? 'primary' : 'secondary'} text-white`}>
              <strong>
                GSA Market
              </strong>
            </CCardHeader>
            <CCardBody>
              <p>
                GSA (General Services Administration) products are available to government agencies and organizations through the GSA Schedule. These products feature government-specific pricing and compliance requirements. 
              </p>
              <div className="small text-muted">
                <strong>
                  Features:
                </strong>{' '}
                Government pricing, Compliance requirements, Special licensing
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={6}>
          <CCard>
            <CCardHeader className={`bg-${activeTab === 'commercial' ? 'success' : 'secondary'} text-white`}>
              <strong>
                Commercial Market
              </strong>
            </CCardHeader>
            <CCardBody>
              <p>
                Commercial products are available to businesses and organizations in the private sector. These products feature standard commercial pricing and are designed for enterprise use. 
              </p>
              <div className="small text-muted">
                <strong>
                  Features:
                </strong>{' '}
                Commercial pricing, Enterprise licensing, Business use
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Product Details Modal */}
      <CModal size="xl" visible={!!selectedProduct} onClose={() => setSelectedProduct(null)}>
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilInfo} className="me-2" />
            Product Details - {selectedProduct?.software_name}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedProduct && (
            <CRow>
              <CCol md={6}>
                <CCard className="h-100">
                  <CCardHeader className="bg-primary text-white">
                    <strong>Product Information</strong>
                  </CCardHeader>
                  <CCardBody>
                    <p><strong>Software ID:</strong><br /><code>{selectedProduct.softwareid}</code></p>
                    <p><strong>Product Name:</strong><br />{selectedProduct.software_name}</p>
                    <p><strong>Type:</strong><br />
                      <CBadge color={getTypeBadge(selectedProduct.type)}>
                        {selectedProduct.type}
                      </CBadge>
                    </p>
                    <p><strong>Market:</strong><br />
                      <CBadge color={selectedProduct.market === 'GSA' ? 'primary' : 'success'}>
                        {selectedProduct.market}
                      </CBadge>
                    </p>
                  </CCardBody>
                </CCard>
              </CCol>
              
              <CCol md={6}>
                <CCard className="h-100">
                  <CCardHeader className="bg-success text-white">
                    <strong>Subscription & Pricing</strong>
                  </CCardHeader>
                  <CCardBody>
                    <p><strong>Subscription:</strong><br />{selectedProduct.subscription}</p>
                    <p><strong>Subscription Type:</strong><br />
                      <CBadge color={getSubscriptionTypeBadge(selectedProduct.subscription_type)}>
                        {selectedProduct.subscription_type}
                      </CBadge>
                    </p>
                    <p><strong>Price:</strong><br />
                      <div className="text-success">
                        <CIcon icon={cilDollar} className="me-1" />
                        <strong>{formatPrice(selectedProduct.price)}</strong>
                      </div>
                    </p>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setSelectedProduct(null)}>
            Close
          </CButton>
        </CModalFooter>
      </CModal>
    </CContainer>
  );
};

export default SoftwareProducts;